import _                                                                                                                      from 'underscore';
import React, { FC, useRef }                                                                                                  from 'react';
import { BarElement, CategoryScale, Chart, Chart as ChartJS, ChartOptions, Legend, LinearScale, Title, Tooltip, TooltipItem } from 'chart.js';
import { Table }                                                                                                              from 'semantic-ui-react';
import { Bar }                                                                                                                from 'react-chartjs-2';

import { Charting }                                                           from '@src/types/charting';
import { CorvalProject, Uuid }                                                from '@src/types';
import { ChartingService, LangService }                                       from '@src/services';
import { mergeDeep, percentage, roundToPrecision }                            from '@src/util';
import { useActivityCountByBusinessFunctionYearlyByProjectChartReportExport } from '@src/hooks';
import { ProjectQueries }                                                     from '@src/queries/ProjectQueries';
import { ACTIVITY_STATUS, EMPTY_VALUE_DISPLAY }                               from '@src/constants';
import { ItemFromQuery }                                                      from '@src/views/components/from-query/ItemFromQuery';
import { SmallGearLoadingMessage }                                            from '@src/views/components/util/loading/SmallGearLoadingMessage';
import { ChartReportWithControlsLayout }                                      from '../ChartReportWithControlsLayout';
import { useSystemData }                                                      from '@src/hooks/useSystemData';


ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

type ReportChartingType = Charting.Reports.ActivityCountByBusinessFunctionYearlyByProject.ChartingType;
type ReportChartingData = Charting.Reports.ActivityCountByBusinessFunctionYearlyByProject.ChartingData;

interface Props
    extends Charting.Reporting.ChartReportBaseProps<ReportChartingType>,
        Charting.Reporting.ChartReportingViewProps<ReportChartingType>,
        Charting.Reporting.ChartReportingExportProps<ReportChartingType> {

    // Project IDs to retrieve data on
    projectIds?: Uuid[],

    // Primary project to compare on
    primaryProjectId?: Uuid | null,

    // Primary project's term dictionary to use as basis for terminology
    termDictionary?: null,

    // Should the chart output have each project's stack broken up by business function,
    // or compile business functions into the tool tip and display as a solid aggregate
    compileBusinessFunctions?: boolean,

    // What business functions to include within the breakdown
    includedBusinessFunctions?: Uuid[],
}

const defaultProps: Props = {
    projectIds:                [],
    title:                     'Yearly Budget Category Data By Business Function Per Project',
    includedBusinessFunctions: [],
    compileBusinessFunctions:  false,
    showTitle:                 true,
    renderAsChart:             false,
    chartOptions:              {},
    datapointFormatter:        ( value: number ) => {
        return ( value > 0 ) ? value : EMPTY_VALUE_DISPLAY;
    },
    itemFromQueryProps:        {
        loadingComponent: () => <SmallGearLoadingMessage label={ 'Loading Project Data...' }/>
    }
};

const ActivityCountByBusinessFunctionYearlyByProject: FC<Props> = ( props ) => {

    const chartRef      = useRef<Chart<ReportChartingType>>(),
          exportMethods = useActivityCountByBusinessFunctionYearlyByProjectChartReportExport( { chartRef, ...props } ),
          systemData    = useSystemData();

    const datasetStatusMap               = useRef( null ),
          legendLabelStackGroupStatusMap = useRef( null );

    const processData = ( data: CorvalProject[] ) => {

        const filterMethod = null;

        return ChartingService.activityCountByBusinessFunctionYearlyByProjectChartingData(
            data,
            filterMethod,
            LangService.getDictionary( props.termDictionary ),
            props.includedBusinessFunctions,
            props.compileBusinessFunctions,
        );
    };

    const getChartOptions = (): ChartOptions<ReportChartingType> => {

        return mergeDeep( {
            plugins:    {
                title:   {
                    text:    props.title,
                    display: props.showTitle,
                    padding: {
                        bottom: 20
                    }
                },
                tooltip: {

                    ...ChartingService.getDefaultChartJSTooltipFormatOptions(),

                    callbacks: {
                        label: formatLabel
                    }
                },
                legend:  {

                    position: 'bottom',

                    // Custom label eventing
                    onClick: handleLegendOnClick,

                    labels: {
                        // Custom label generation
                        generateLabels: generateCustomLabels,
                    },
                }
            },
            responsive: true,
            scales:     {
                x: { stacked: true, },
                y: { stacked: true, ticks: { callback: formatChartYTick } },
            }

        } as ChartOptions<ReportChartingType>, props.chartOptions ?? {} );
    };

    // Custom label generation function
    // Custom label generation function
    function generateCustomLabels( chart ){

        const { datasets } = chart.data;

        let projectDataSet: Array<{
            color: string,
            label: string,
            stack: string,
        }> = datasets.map( dataset => {

            return {
                color: dataset.extendedData.projectColor,
                label: dataset.extendedData.projectName,
                stack: dataset.stack,
            };
        } );

        // Reset hidden status array
        const uniqueStackGroupDataSets = new Map<string, { index: number, projectInfo: typeof projectDataSet[0] }>( [] );

        projectDataSet.map( ( projectInfo, index ) => {

            uniqueStackGroupDataSets.set( projectInfo.stack, { index, projectInfo } );

        } );

        const legendLabelStackGroupDataSets = Array.from( uniqueStackGroupDataSets );

        // set all to "not hidden"
        if( _.isNull( legendLabelStackGroupStatusMap.current ) ){
            legendLabelStackGroupStatusMap.current = new Array( legendLabelStackGroupDataSets.length ).fill( false );
        }

        datasetStatusMap.current = new Array( datasets.length ).fill( false );

        // define the labels
        const stackTypeLabels = legendLabelStackGroupDataSets.map( ( uniqueDataSet, uniqueDataSetIndex ) => {

            const [ stackGroup, { index, projectInfo } ] = uniqueDataSet;

            return ( {
                text:        projectInfo.label, // Display project name label group as legend label
                hidden:      legendLabelStackGroupStatusMap.current[ uniqueDataSetIndex ],
                toggleType:  'stackGroup',
                statusIndex: uniqueDataSetIndex,
                stackGroup:  stackGroup,
                fillStyle:   projectInfo.color,
                strokeStyle: 'transparent',
            } );
        } );

        return [
            ...stackTypeLabels,
        ];
    }

    // Legend click event handler
    function handleLegendOnClick( event, legendItem, { chart } ){

        if( legendItem.toggleType === 'stackGroup' ){

            // update the legend label
            legendLabelStackGroupStatusMap.current[ legendItem.statusIndex ] = !legendLabelStackGroupStatusMap.current[ legendItem.statusIndex ];

            const statusValue = legendLabelStackGroupStatusMap.current[ legendItem.statusIndex ];

            // update the real datasets that match this stack group
            chart.data.datasets.map( ( dataset, index ) => {

                const meta = chart.getDatasetMeta( index );

                if( dataset.stack === legendItem.stackGroup ){

                    // Toggle hidden status of clicked dataset
                    datasetStatusMap.current[ index ] = statusValue;
                    meta.hidden = statusValue;
                }
            } );
        }

        // Update chart to reflect changes
        chart.update();
    }

    const formatDatapoint = ( datapoint ) => {

        return _.isFunction( props.datapointFormatter )
               ? props.datapointFormatter( datapoint )
               : defaultProps.datapointFormatter( datapoint );
    };

    const formatLabel = ( tooltipItem: TooltipItem<any> ) => {

        const displayingCompiledData = props.compileBusinessFunctions;
        const label = tooltipItem.dataset.label,
              value = tooltipItem.parsed.y;

        const pluralizeNoun = value => ( value == 1 ) ? 'Activity' : 'Activities';

        const generateBreakdownData = ( datasets ) => {

            return _.values( datasets ).map( dataSet => {

                const breakdownValue = dataSet.data[ tooltipItem.parsed.x ],
                      percentOfTotal = roundToPrecision( percentage( breakdownValue, value ), 2 );

                return ( breakdownValue > 0 )
                       ? `• ${ dataSet.label }:  ${ formatDatapoint( breakdownValue ) }  (${ percentOfTotal }%)`
                       : `• ${ dataSet.label }:  ${ formatDatapoint( breakdownValue ) }`;
            } );

        };

        if( displayingCompiledData ){

            const headerString = `${ label }:  ${ formatDatapoint( value ) } ${ pluralizeNoun( value ) } initiated`;
            const breakdownData = generateBreakdownData( tooltipItem.dataset.extendedData.businessFunctionBreakdown );

            // label as project name
            return [ headerString, ' ', ...breakdownData ];

        } else {

            return [
                `${ label }`,
                `${ tooltipItem.formattedValue } ${ pluralizeNoun( value ) } initiated`
            ];
        }
    };

    const formatChartYTick = ( value, index, ticks ) => {

        return formatDatapoint( value );
    };

    return (
        <ItemFromQuery
            as={ React.Fragment }
            query={ ProjectQueries.queries.GET_PROJECTS( `
				id
                name
                projectActivities {
					id
					completionState,
					startTimeUtc
					isRecurrence
	                displayName
					activity{
		                id
		                businessFunction
					}
                }`
            ) }
            queryOptions={ {
                variables: {
                    where: {
                        and: [
                            { id: { in: props.projectIds } },
                            { isActive: { eq: true } }
                        ]
                    }
                }
            } }
            useLoadingComponent={ true }
            queryResultObject={ ( data ) => {

                const projects = data.projects as CorvalProject[];

                projects.map( project => {

                    project.projectActivities = project.projectActivities.filter( projectActivity => {

                        const isActive = ( projectActivity.completionState as unknown as ACTIVITY_STATUS !== ACTIVITY_STATUS.EXCLUDED );
                        // const isRecurrence = projectActivity.isRecurrence;

                        return ( isActive );

                    } );

                    return project;
                } );

                return processData( projects );

            } }
            { ...props.itemFromQueryProps }>

            { ( data: ReportChartingData ) => {

                /* Use CSV data to generate the complete grid, then edit down */
                const tableData = exportMethods.generateCSVData( data );
                const tableKeys = _.chain( tableData.headers )
                                   .pluck( 'key' )
                                   .filter( key => {

                                       if( !props.includedBusinessFunctions ) return true;

                                       return (
                                           !_.pluck( systemData.businessFunctions, 'id' ).includes( key ) ||
                                           props.includedBusinessFunctions.includes( key )
                                       );
                                   } )
                                   .value();
                const tableHeaders = _.filter( tableData.headers, header => tableKeys.includes( header.key ) );

                return (

                    <ChartReportWithControlsLayout<ReportChartingType, ReportChartingData>

                        { ...props }

                        data={ data }
                        exportMethods={ exportMethods }
                        chartComponent={

                            <Bar ref={ chartRef } options={ getChartOptions() } data={ data }/>
                        }
                        tableComponent={

                            <div style={ { overflow: 'auto', width: '100%', maxHeight: 500 } }>

                                <Table basic={ 'very' } size={ 'small' } striped definition className={ 'position--relative' }>

                                    <Table.Header className={ 'position--sticky bg--colored-white' } style={ { top: 0 } }>
                                        <Table.Row>

                                            { tableHeaders.map( ( header, index ) => (
                                                <Table.HeaderCell verticalAlign={ 'bottom' }>{ header.label }</Table.HeaderCell>
                                            ) ) }

                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>

                                        { tableData.data.map( ( row, rowIndex ) => {

                                            return (

                                                <Table.Row key={ rowIndex }>

                                                    { tableKeys.map( ( key, columnIndex ) => (
                                                        <Table.Cell collapsing={ true } key={ `${ key }-${ rowIndex }-${ columnIndex }` }>
                                                            { row[ key ] }
                                                        </Table.Cell>
                                                    ) ) }

                                                </Table.Row>
                                            );
                                        } ) }
                                    </Table.Body>
                                </Table>

                            </div>
                        }
                    />
                );
            } }

        </ItemFromQuery>
    );
};

ActivityCountByBusinessFunctionYearlyByProject.defaultProps = defaultProps;

export { ActivityCountByBusinessFunctionYearlyByProject };
